import { useEffect } from 'react';
import { Alert, Form, InputNumber, Modal, Select, Switch, TimePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { Branch } from '../catalog/api';
import {
  WEEKDAY_OPTIONS,
  type ReservationSlot,
  type UpsertReservationSlotPayload,
  type Weekday,
} from './api';

/** `TimeOnly` trên dây là `HH:mm:ss`; hiển thị tới phút vì model không lưu giây. */
const WIRE_TIME = 'HH:mm:ss';
const DISPLAY_TIME = 'HH:mm';

interface FormValues {
  branchId: string;
  daysOfWeek: Weekday[];
  startsAt: Dayjs;
  endsAt: Dayjs;
  capacity: number;
  isActive: boolean;
}

interface Props {
  slot: ReservationSlot | null;
  isCreating: boolean;
  branches: Branch[];
  submitting: boolean;
  onSubmit: (payload: UpsertReservationSlotPayload) => void;
  onClose: () => void;
}

function buildPayload(values: FormValues): UpsertReservationSlotPayload {
  return {
    branchId: values.branchId,
    daysOfWeek: values.daysOfWeek ?? [],
    startsAt: values.startsAt.format(WIRE_TIME),
    endsAt: values.endsAt.format(WIRE_TIME),
    capacity: values.capacity,
    isActive: values.isActive ?? true,
  };
}

export function ReservationSlotFormModal({
  slot,
  isCreating,
  branches,
  submitting,
  onSubmit,
  onClose,
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const open = slot !== null || isCreating;

  useEffect(() => {
    if (!open) return;

    if (slot) {
      form.setFieldsValue({
        branchId: slot.branchId,
        // Server trả đủ bảy thứ khi khung áp mọi ngày. Giữ nguyên thì ô hiện bảy thẻ và người
        // sửa tưởng đã chọn tay từng thứ — rút về rỗng, nghĩa vẫn y hệt.
        daysOfWeek: slot.daysOfWeek.length === 7 ? [] : slot.daysOfWeek,
        startsAt: dayjs(slot.startsAt, WIRE_TIME),
        endsAt: dayjs(slot.endsAt, WIRE_TIME),
        capacity: slot.capacity,
        isActive: slot.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ daysOfWeek: [], capacity: 10, isActive: true });
    }
  }, [slot, open, form]);

  return (
    <Modal
      open={open}
      title={isCreating ? 'Thêm khung giờ nhận đặt' : 'Sửa khung giờ'}
      okText="Lưu"
      cancelText="Hủy"
      width={560}
      confirmLoading={submitting}
      destroyOnHidden
      onCancel={onClose}
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(buildPayload(values)))
          .catch(() => {});
      }}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="branchId"
          label="Chi nhánh"
          rules={[{ required: true, message: 'Chưa chọn chi nhánh.' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            // Khung giờ gắn cứng một chi nhánh (khóa ngoại). Sửa thì giữ nguyên chi nhánh để
            // không vô tình dời khung sang cửa khác.
            disabled={!isCreating}
            placeholder="Chọn chi nhánh"
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
          />
        </Form.Item>

        <Form.Item
          name="daysOfWeek"
          label="Thứ trong tuần"
          extra="Để trống = mọi ngày."
        >
          <Select mode="multiple" allowClear options={WEEKDAY_OPTIONS} placeholder="Mọi ngày" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item
            name="startsAt"
            label="Từ giờ"
            rules={[{ required: true, message: 'Chưa nhập giờ mở.' }]}
          >
            <TimePicker format={DISPLAY_TIME} minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="endsAt"
            label="Tới giờ"
            dependencies={['startsAt']}
            rules={[
              { required: true, message: 'Chưa nhập giờ đóng.' },
              {
                validator: (_, value: Dayjs | undefined) => {
                  const from = form.getFieldValue('startsAt') as Dayjs | undefined;
                  if (!value || !from) return Promise.resolve();
                  // RL-271 (PL chốt với QA 2026-10-14): khung vắt nửa đêm làm trượt thứ trong
                  // tuần và chia đôi phép đếm trần theo ngày VN — gấp đôi trần, overbooking. Tạm
                  // chỉ chặn ở Admin Web; backend và `ck_reservation_slots_window` giữ nguyên.
                  // Ca đêm thì khai hai khung (22:00–23:55 và 00:00–02:00), cùng lối BR-407.
                  if (value.format(WIRE_TIME) <= from.format(WIRE_TIME)) {
                    return Promise.reject(
                      new Error(
                        'Giờ kết thúc phải lớn hơn giờ bắt đầu trong cùng một ngày. Hệ thống tạm thời chưa hỗ trợ khung giờ vắt qua nửa đêm.',
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker format={DISPLAY_TIME} minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
        </div>

        <Form.Item
          name="capacity"
          label="Số đơn tối đa mỗi khung, mỗi ngày"
          extra="Không nhận đặt trước 100% số bàn — nên chừa khoảng 30% cho khách vãng lai."
          rules={[{ required: true, message: 'Chưa nhập số đơn tối đa.' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Đang nhận đặt"
          valuePropName="checked"
          extra="Tắt thì ngừng nhận đơn mới ở khung này, nhưng không xóa (đơn cũ vẫn còn căn cứ)."
        >
          <Switch />
        </Form.Item>

        <Alert
          type="warning"
          showIcon
          message="Giờ tính theo giờ Việt Nam"
          description="Hai khung cùng chi nhánh không được trùng giờ trên các thứ chung — server sẽ chặn nếu chồng nhau."
        />
      </Form>
    </Modal>
  );
}
